import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Page-entry fade: replays a CSS opacity animation on the page's <main> whenever the pathname changes
 * (incl. detail slug changes). Hash-only changes do not replay. Header/Footer sit outside <main>.
 * Opacity only — no transform/filter, so fixed/sticky/ScrollTrigger measurements are unaffected.
 * Uses `backwards` fill, so content is never left hidden once the animation ends or if it never runs.
 */
export default function RouteFade() {
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    const main = document.querySelector('main');
    if (!main) return;
    document.querySelectorAll('.route-fade').forEach((el) => el.classList.remove('route-fade'));
    // Dark blocks marked [data-route-dark] keep their dark base visible; only their contents fade,
    // so the light page background never shows through them (no white flash).
    const targets: Element[] = main.querySelector('[data-route-dark]')
      ? Array.from(main.children).flatMap((c) => (c.hasAttribute('data-route-dark') ? Array.from(c.children) : [c]))
      : [main];
    // force reflow so the animation restarts when elements are reused
    main.getBoundingClientRect();
    targets.forEach((el) => el.classList.add('route-fade'));

    // Home starts on a black spotlight screen: keep the canvas black during the fade to avoid a white flash.
    const root = document.documentElement;
    if (pathname !== '/') {
      root.classList.remove('route-dark-canvas');
      return undefined;
    }
    root.classList.add('route-dark-canvas');
    const clear = () => root.classList.remove('route-dark-canvas');
    main.addEventListener('animationend', clear, { once: true });
    const timer = window.setTimeout(clear, 400);
    return () => {
      window.clearTimeout(timer);
      main.removeEventListener('animationend', clear);
      clear();
    };
  }, [pathname]);

  return null;
}